import { datadogLogs } from '@datadog/browser-logs';
import environment from '@department-of-veterans-affairs/platform-utilities/environment';
import { initializeBrowserLogging } from 'platform/monitoring/Datadog';
import { getConversationIdKey } from './sessionStorage';

const APP_ID = 'ad327652-564b-4537-91c7-328eb90f77ed';
const CLIENT_TOKEN = 'pubf64b43174e3eb74fa640b1ec28781c07';

// The Datadog browser-logs SDK captures errors for the *whole page*, not just
// this app: `forwardErrorsToLogs` hooks `window.onerror`, `unhandledrejection`,
// and failed XHR/fetch requests across the document. Everything it captures is
// then tagged `service:virtual-agent-front-end` and alerts the chatbot team,
// including failures that belong to the platform or to third-party tooling.
//
// We previously tried to subtract that noise with a denylist of error message
// patterns (see software/va-chatbot#661), but matching on message text only
// works while the text is present and phrased the way we predicted, so it kept
// springing leaks. `beforeSend` instead decides on `origin`, a structured field
// the SDK always sets, so the decision does not depend on message content:
//
//   logger                     -> forward. Our own deliberate logErrorToDatadog
//                                 calls, including the chatbot error boundary.
//   network                    -> forward only for Direct Line, the one backend
//                                 whose failures we cannot catch ourselves,
//                                 except `status_code: 0`. Turns out that's
//                                 client-side network blocks (corporate
//                                 firewalls/proxies, VPNs, see
//                                 software/va-chatbot#663), nothing we can
//                                 act on. A failed activity post (sending a
//                                 message) still gets forwarded, though, see
//                                 isActivityRequest.
//   source / console / report  -> drop. Page-wide capture we cannot attribute
//                                 to the chatbot.
//
// Dropping `source` means an uncaught error thrown inside chatbot code is no
// longer reported automatically, which is why the chatbot subtree is wrapped in
// an error boundary that reports render and lifecycle crashes itself.

// Direct Line is the only host worth forwarding automatically: the library
// handles its own transport errors internally, so a failed call to the
// conversation channel never reaches our code -- the SDK's network capture is
// our only visibility into "the conversation could not start".
//
// The VA API is deliberately NOT listed. The one call the chatbot makes to it
// (`apiRequest('/chatbot/token')`) already reports from its own catch via
// logErrorToDatadog, so listing the host would add nothing for our own traffic
// while admitting platform-owned requests we never make -- /v0/user,
// /v0/feature_toggles, /v0/maintenance_windows -- and blaming the chatbot for
// their failures.
const CHATBOT_HOSTS = ['directline.botframework.com'];

// Match a host exactly or as a subdomain (northamerica.directline.botframework.com),
// but not a lookalike suffix (directline.botframework.com.evil.example).
const isChatbotHost = host =>
  CHATBOT_HOSTS.some(domain => host === domain || host.endsWith(`.${domain}`));

// POST /conversations/{id}/activities (sending a message) is one request
// per user action, not part of the connection retry storm. And
// ChatConversationComposerBridge.jsx just swallows the send error into
// useDeferredPostError without logging it anywhere, so this is genuinely
// the only record we have that a Veteran's message failed to send. Don't
// drop it along with the status_code: 0 noise.
const isActivityRequest = pathname => /\/activities\/?$/.test(pathname);

/**
 * Datadog browser-logs `beforeSend`: return true to forward a log, false to
 * discard it.
 *
 * @param {Object} event Datadog log event
 * @returns {boolean} whether to forward the log
 */
export function shouldForwardLog(event) {
  const origin = event?.origin;

  // Anything we chose to log about ourselves.
  if (origin === 'logger') {
    return true;
  }

  // A failed request. Forward it only if we were the one making it.
  if (origin === 'network') {
    const url = event?.http?.url;
    if (!url) {
      return false;
    }
    let parsedUrl;
    try {
      parsedUrl = new URL(url);
    } catch {
      return false;
    }
    if (!isChatbotHost(parsedUrl.hostname)) {
      return false;
    }

    // `status_code: 0` means the request never got an HTTP response at
    // all, a client-side transport failure rather than anything Direct
    // Line returned. We pulled the Datadog data for software/va-chatbot#663
    // and found these cluster almost entirely on a handful of corporate
    // networks (Menlo Security was 100% failures in the sample), so there's
    // nothing for us to fix and we just drop them. A real Direct Line
    // response (4xx/5xx) is a genuine service-side signal, so that always
    // gets forwarded, and so does a failed activity post (see
    // isActivityRequest).
    return (
      event?.http?.status_code !== 0 || isActivityRequest(parsedUrl.pathname)
    );
  }

  // Uncaught page errors and console.error, from anywhere on the page.
  return false;
}

export const initLogging = () =>
  initializeBrowserLogging({
    clientToken: CLIENT_TOKEN,
    applicationId: APP_ID,
    service: 'virtual-agent-front-end',
    site: 'ddog-gov.com',
    env: environment.vspEnvironment(),
    sessionSampleRate: 100,
    // We want none of the page's console errors. Note the SDK re-adds 'error'
    // to this list whenever forwardErrorsToLogs is on, so this alone does not
    // stop them -- `shouldForwardLog` dropping origin 'console' is what does.
    forwardConsoleLogs: [],
    telemetrySampleRate: 100,
    // Required: network-error collection is gated on this flag too, so turning
    // it off would also lose failed Direct Line requests.
    forwardErrorsToLogs: true,
    forwardReports: [],
    sampleRate: 100,
    version: '1.0.0',
    beforeSend: shouldForwardLog,
  });

function readConversationId() {
  try {
    return getConversationIdKey();
  } catch (e) {
    return null;
  }
}

function normalizeContext(context) {
  if (!context || typeof context !== 'object' || context instanceof Error) {
    return {};
  }

  return { ...context };
}

export function logErrorToDatadog(
  isDatadogLoggingEnabled,
  message,
  context = {},
) {
  if (isDatadogLoggingEnabled) {
    const error = context instanceof Error ? context : undefined;
    const logContext = normalizeContext(context);
    const conversationId = readConversationId();

    if (conversationId) {
      // eslint-disable-next-line camelcase
      logContext.conversation_id = conversationId;
    }

    datadogLogs.logger.error(message, logContext, error);
  }
}
